import type { CSSProperties, ReactNode } from 'react'

export type SeverityKind = 'high' | 'medium' | 'info'

interface SeverityProps {
  kind: SeverityKind
  children?: ReactNode
  style?: CSSProperties
}

const KIND_STYLES: Record<SeverityKind, { bg: string; color: string }> = {
  high: { bg: 'var(--danger-bg)', color: 'var(--danger-fg)' },
  medium: { bg: 'var(--warning-bg)', color: 'var(--warning-fg)' },
  info: { bg: 'var(--surface-2)', color: 'var(--text-2)' },
}

/**
 * Dot-pill severity indicator.
 * kind: 'high' → danger, 'medium' → warning, 'info' → neutral.
 */
export function Severity({ kind, children, style }: SeverityProps): JSX.Element {
  const { bg, color } = KIND_STYLES[kind]

  return (
    <span
      data-testid="severity"
      data-kind={kind}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 9px',
        borderRadius: 4,
        background: bg,
        color,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'currentColor',
          display: 'inline-block',
          flexShrink: 0,
        }}
      />
      {children ?? kind}
    </span>
  )
}
